import * as XLSX from "xlsx";
import {
  MIN_VISIBLE_COLS, MIN_VISIBLE_ROWS,
  EXTRA_SCROLL_ROWS, EXTRA_SCROLL_COLS,
  DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT,
} from "../constants";
import { resolveExcelColorToCss } from "../utils/colorUtils";
import { getColumnWidthPx, getRowHeightPx } from "../utils/columnUtils";

// SheetJS stores the autoFilter range in ws["!autofilter"].ref ("A1:D100").
// Return the 0-based column span it covers, or null.
function parseAutoFilterCols(ws) {
  const ref = ws?.["!autofilter"]?.ref;
  if (!ref) return null;
  try {
    const r = XLSX.utils.decode_range(ref);
    return { c1: Math.min(r.s.c, r.e.c), c2: Math.max(r.s.c, r.e.c) };
  } catch (_) {
    return null;
  }
}

export function buildSheetModel(ws) {
  const ref = ws?.["!ref"] || "A1";
  const range = XLSX.utils.decode_range(ref);
  const dataRowsCount = Math.max(range.e.r + 1, 1);
  const dataColsCount = Math.max(range.e.c + 1, 1);
  const targetCols = Math.max(dataColsCount + EXTRA_SCROLL_COLS, MIN_VISIBLE_COLS);
  const targetRows = Math.max(dataRowsCount + EXTRA_SCROLL_ROWS, MIN_VISIBLE_ROWS);

  const rows = new Array(targetRows).fill(null).map(() => new Array(targetCols).fill(""));
  const formulas = {};
  const styles = {};
  const links = {};   // key: "r:c" → URL string

  for (let r = 0; r <= range.e.r; r += 1) {
    for (let c = 0; c <= range.e.c; c += 1) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = ws[addr];
      if (!cell) continue;

      rows[r][c] = cell.w ?? cell.v ?? "";
      if (cell.f) formulas[`${r}:${c}`] = `=${cell.f.replace(/_xlfn\.|_xludf\./g, "")}`;

      // Hyperlinks (SheetJS stores in cell.l with .Target)
      if (cell.l && typeof cell.l.Target === "string" && cell.l.Target.trim()) {
        links[`${r}:${c}`] = cell.l.Target.trim();
      }

      // Default right-alignment for numeric/date cells that have NO style record.
      // Cells with a style record are handled inside the `if (s)` block below.
      if (!cell.s && (cell.t === "n" || cell.t === "d")) {
        styles[`${r}:${c}`] = { textAlign: "right" };
      }

      const s = cell.s;
      if (s) {
        const css = {};
        const fontColor = resolveExcelColorToCss(s.font?.color, null);
        if (fontColor) css.color = fontColor;
        if (s.font?.bold) css.fontWeight = 700;
        if (s.font?.italic) css.fontStyle = "italic";
        const textDecorations = [];
        if (s.font?.underline) textDecorations.push("underline");
        if (s.font?.strike) textDecorations.push("line-through");
        if (textDecorations.length > 0) css.textDecoration = textDecorations.join(" ");
        if (typeof s.font?.name === "string") css.fontFamily = `"${s.font.name}", sans-serif`;
        if (typeof s.font?.sz === "number") css.fontSize = `${Math.max(8, Math.round(s.font.sz * 96 / 72))}px`;
        // Fill: only apply for real fills, not default empty patterns
        const fgColor = resolveExcelColorToCss(s.fill?.fgColor, null);
        const fillPattern = s.fill?.patternType || "";
        if (fgColor && fillPattern !== "none" && fillPattern !== "gray125" && fillPattern !== "") {
          css.backgroundColor = fgColor;
        }
        // Alignment
        const al = s.alignment || {};
        if (al.horizontal) {
          const hMap = { left: "left", center: "center", right: "right", fill: "left", justify: "justify", centerContinuous: "center", distributed: "justify" };
          css.textAlign = hMap[al.horizontal] || al.horizontal;
        }
        if (al.vertical) {
          const vMap = { top: "top", center: "middle", bottom: "bottom", distributed: "middle", justify: "top" };
          css.verticalAlign = vMap[al.vertical] || al.vertical;
        }
        if (al.wrapText) css.whiteSpace = "pre-wrap";

        // Auto-detect line breaks: force pre-wrap when the cell value contains
        // newlines so multi-line content displays as it does in Excel.
        if (!al.wrapText) {
          const rawStr = cell.w ?? cell.v ?? "";
          if (typeof rawStr === "string" && /\r|\n/.test(rawStr)) {
            css.whiteSpace = "pre-wrap";
          }
        }

        // Default right-alignment for numeric/date cells — mirrors Excel's built-in behaviour.
        // cell.t: 'n' = number, 'd' = date. Only apply when no explicit alignment stored.
        if (!css.textAlign && (cell.t === "n" || cell.t === "d")) {
          css.textAlign = "right";
        }

        // Borders (SheetJS stores under cell.s.border with .style and .color per side)
        const sb = s.border || {};
        const sbStyleMap = {
          thin: "1px solid", medium: "2px solid", thick: "3px solid",
          dashed: "1px dashed", dotted: "1px dotted", double: "3px double",
          hair: "1px solid", mediumDashed: "2px dashed", dashDot: "1px dashed",
          mediumDashDot: "2px dashed", slantDashDot: "1px dashed",
          dashDotDot: "1px dashed", mediumDashDotDot: "2px dashed",
        };
        ["top", "right", "bottom", "left"].forEach((side) => {
          const b = sb[side];
          if (b && b.style) {
            const bStyle = sbStyleMap[b.style] || "1px solid";
            const bColor = resolveExcelColorToCss(b.color, null) || "#000000";
            const prop = `border${side.charAt(0).toUpperCase()}${side.slice(1)}`;
            css[prop] = `${bStyle} ${bColor}`;
          }
        });

        if (Object.keys(css).length > 0) styles[`${r}:${c}`] = css;
      }
    }
  }

  // Border propagation: mirror each cell's border onto the adjacent cell's
  // opposite side so that border-collapse tiebreakers (source order) always
  // resolve to the correct Excel border colour/weight.
  const getSBPx = (v) => { if (!v) return 0; const m = v.match(/^(\d+)px/); return m ? +m[1] : 1; };
  Object.keys(styles).forEach((key) => {
    const [r, c] = key.split(":").map(Number);
    const s2 = styles[key];
    const spread = (fromProp, adjR, adjC, toProp) => {
      const val = s2[fromProp];
      if (!val) return;
      if (adjR < 0 || adjC < 0 || adjR >= targetRows || adjC >= targetCols) return;
      const ak = `${adjR}:${adjC}`;
      const adj = styles[ak] || {};
      if (getSBPx(val) >= getSBPx(adj[toProp])) { adj[toProp] = val; styles[ak] = adj; }
    };
    spread("borderTop",    r - 1, c,     "borderBottom");
    spread("borderLeft",   r,     c - 1, "borderRight");
    spread("borderBottom", r + 1, c,     "borderTop");
    spread("borderRight",  r,     c + 1, "borderLeft");
  });

  const rawCols = Array.isArray(ws?.["!cols"]) ? ws["!cols"] : [];
  const rawRows = Array.isArray(ws?.["!rows"]) ? ws["!rows"] : [];
  const colWidths = Array.from({ length: targetCols }, (_, i) => getColumnWidthPx(rawCols[i]));
  const rowHeights = Array.from({ length: targetRows }, (_, i) => getRowHeightPx(rawRows[i]));

  // Merges — SheetJS stores in ws["!merges"] as an array of { s: {r,c}, e: {r,c} }
  const merges = {};
  const rawMerges = ws?.["!merges"] || [];
  rawMerges.forEach((m) => {
    const r1 = m.s.r, c1 = m.s.c, r2 = m.e.r, c2 = m.e.c;
    merges[`${r1}:${c1}`] = { rowSpan: r2 - r1 + 1, colSpan: c2 - c1 + 1 };
    for (let mr = r1; mr <= r2; mr++) {
      for (let mc = c1; mc <= c2; mc++) {
        if (mr !== r1 || mc !== c1) merges[`${mr}:${mc}`] = null; // slave
      }
    }
  });

  // Freeze panes — SheetJS doesn't parse pane data, so default to 0.
  // For .xlsx files ExcelJS handles freeze extraction; this path is the .xls fallback.
  const frozenRows = 0;
  const frozenCols = 0;

  return { rows, maxCols: targetCols, colWidths, rowHeights, formulas, styles, links, merges, richHtml: {}, images: [], charts: [], dataValidations: {}, frozenRows, frozenCols, dataRowCount: dataRowsCount, dataColCount: dataColsCount, sortState: null, autoFilterCols: parseAutoFilterCols(ws) };
}
